import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Loader2, Plus, TrendingUp, TrendingDown, Minus,
    FileSpreadsheet, RefreshCw, AlertTriangle, Flame,
    Sparkles, Target, FileDown, Share2, Trash2, Copy as CopyIcon,
    Calendar, Filter as FilterIcon, Lock,
    ClipboardCheck, CheckCircle2, XCircle, AlertCircle,
    ChevronDown, Check, Zap, X,
} from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/nav/AppShell";
import { api } from "@/lib/api";
import { useUsage } from "@/hooks/useUsage";
import { UpgradeModal } from "@/components/UsageWidget";
import WeeklyAnalysisCard from "@/components/WeeklyAnalysisCard";


// ╔════════════════════════════════════════════════════════════════════════╗
// ║ PR #48 — Helpers for action checkbox state + TL;DR top actions         ║
// ╚════════════════════════════════════════════════════════════════════════╝

/**
 * Stable client-side hash of a recommendation. Two reports must NEVER share
 * action_ids, but the same item rendered twice in one report must produce the
 * same id. We hash category + the most-stable text field. Length is bounded
 * (server-side validates ≤128 chars).
 *
 *   actionId("neg", "darmowy")          → "neg:7c9f..."
 *   actionId("budget", "from→to:50→0")  → "budget:a1b2..."
 */
const actionId = (category, payload) => {
    const str = String(payload || "").slice(0, 200);
    // djb2-ish hash → hex, short. Good enough for collision avoidance within
    // a single report (≤100 actions).
    let h = 5381;
    for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) | 0;
    const hex = (h >>> 0).toString(16).padStart(8, "0");
    return `${category}:${hex}`;
};

const DoneContext = React.createContext({
    isDone: () => false,
    toggle: () => {},
    busy: new Set(),
});

/** Checkbox button. Renders an empty square when not done, a filled check
 *  when done. Click toggles state and triggers an API round-trip via
 *  DoneContext.toggle. Optimistic UI — flips immediately, reverts on error. */
const DoneCheckbox = ({ actionId: aid, label = "Виконано" }) => {
    const { isDone, toggle, busy } = React.useContext(DoneContext);
    const done = isDone(aid);
    const isBusy = busy.has(aid);
    // PR #53: visible tooltip on hover so users discover the feature.
    // Previously only aria-label existed → invisible to sighted users.
    const tooltip = done
        ? "Натисни, щоб скасувати позначку"
        : "Позначити як виконано — зникне зі списку наступного звіту";
    return (
        <button
            type="button"
            onClick={(e) => { e.stopPropagation(); toggle(aid); }}
            disabled={isBusy}
            aria-label={done ? "Скасувати позначку" : label}
            title={tooltip}
            data-testid={`action-done-${aid}`}
            className={`shrink-0 inline-flex items-center justify-center w-5 h-5 rounded border transition
                ${done
                    ? "bg-success/20 border-success/50 text-success-c"
                    : "border-border hover:border-primary/50 hover:bg-primary/5 text-faint-c hover:text-primary-c"}
                ${isBusy ? "opacity-50 cursor-wait" : "cursor-pointer"}`}
        >
            {done ? <Check size={12} strokeWidth={3} /> : null}
        </button>
    );
};

/** Wrap any recommendation card. Adds a checkbox + faded styling when done.
 *  Pass action_id derived via actionId() helper. */
const ActionRow = ({ actionId: aid, children }) => {
    const { isDone } = React.useContext(DoneContext);
    const done = isDone(aid);
    return (
        <div className={`flex items-start gap-3 transition ${done ? "opacity-50" : ""}`}>
            <div className="pt-3">
                <DoneCheckbox actionId={aid} />
            </div>
            <div className={`flex-1 min-w-0 ${done ? "line-through decoration-success/40" : ""}`}>
                {children}
            </div>
        </div>
    );
};

/** Build the TL;DR top-3 actions list from a report's analysis payload.
 *  Heuristic: gather ALL items across recommendation groups + wasted items,
 *  filter to priority='urgent', then take top 3 by category importance:
 *      negative_keywords > budget_reallocation > pause_keywords >
 *      ad_copy_rewrites > new_keywords_to_add
 *  This roughly mirrors "biggest bleed first, then highest-impact fixes". */
const extractTopActions = (analysis) => {
    const recs = analysis?.recommendations || {};
    const priorityOrder = {
        negative_keywords:    1,
        budget_reallocation:  2,
        pause_keywords:       3,
        ad_copy_rewrites:     4,
        new_keywords_to_add:  5,
    };
    const all = [];
    for (const [groupKey, items] of Object.entries(recs)) {
        if (!Array.isArray(items)) continue;
        for (const it of items) {
            if ((it.priority || "").toLowerCase() !== "urgent") continue;
            all.push({ groupKey, item: it });
        }
    }
    all.sort((a, b) => (priorityOrder[a.groupKey] || 99) - (priorityOrder[b.groupKey] || 99));

    return all.slice(0, 3).map(({ groupKey, item }) => {
        if (groupKey === "negative_keywords") {
            return {
                aid: actionId("neg", item.keyword),
                title: `Додати мінус-слово «${item.keyword}»`,
                impact: item.expected_impact,
            };
        }
        if (groupKey === "budget_reallocation") {
            return {
                aid: actionId("budget", `${item.action}:${item.from}→${item.to}`),
                title: `Бюджет: ${item.action || ""} ${item.from || ""} → ${item.to || ""}`.trim(),
                impact: item.expected_impact || item.amount,
            };
        }
        if (groupKey === "pause_keywords") {
            return {
                aid: actionId("pause", item.keyword),
                title: `Поставити на паузу «${item.keyword}»`,
                impact: item.expected_impact,
            };
        }
        if (groupKey === "ad_copy_rewrites") {
            return {
                aid: actionId("adcopy", item.ad_group || item.target || JSON.stringify(item).slice(0, 60)),
                title: `Переписати оголошення: ${item.ad_group || item.target || "група"}`,
                impact: item.expected_impact,
            };
        }
        if (groupKey === "new_keywords_to_add") {
            return {
                aid: actionId("newkw", item.keyword),
                title: `Додати ключ «${item.keyword}»`,
                impact: item.expected_impact,
            };
        }
        return null;
    }).filter(Boolean);
};

/** Count total recommendations + how many are done. Used in section headers. */
/**
 * PR #55: detect items about REMOVED/PAUSED campaigns. Used both as a filter
 * in WhatBurns and as a deduction in section count badges, so the header
 * count matches what's actually rendered. */
const _isAboutDeadCampaign = (it) => {
    if (!it) return false;
    const blob = [
        it.name, it.title, it.detail, it.text, it.term, it.campaign,
        it.metric_snapshot, it.issue, it.reason,
    ].filter(Boolean).join(" ");
    return /\b(REMOVED|PAUSED)\b/.test(blob);
};

const countRecs = (data, { excludeDeadCampaigns = false } = {}) => {
    if (!data) return { total: 0 };
    let total = 0;
    for (const v of Object.values(data)) {
        if (!Array.isArray(v)) continue;
        total += excludeDeadCampaigns
            ? v.filter((it) => !_isAboutDeadCampaign(it)).length
            : v.length;
    }
    return { total };
};


export default function Reports() {
    const { reportId } = useParams();
    return reportId ? <ReportDetailPage reportId={reportId} /> : <ReportListPage />;
}

// ╔════════════════════════════════════════════════════════════════════════╗
// ║ LIST PAGE                                                              ║
// ╚════════════════════════════════════════════════════════════════════════╝
function ReportListPage() {
    const navigate = useNavigate();
    const { getLimit } = useUsage();
    const csvLimit = getLimit("csv_analyses");
    const [items, setItems] = useState(null);
    const [strategy, setStrategy] = useState(null);
    const [filterDate, setFilterDate] = useState("all");
    const [filterScore, setFilterScore] = useState("all");
    const [filterSource, setFilterSource] = useState("all");

    // Ref to the inline WeeklyAnalysisCard for smooth-scroll when the
    // user clicks "Create new report" or the empty-state CTA. Keeps the
    // user on this page instead of bouncing to /dashboard (PR #47 fix).
    const analysisRef = React.useRef(null);

    useEffect(() => {
        api.get("/reports?limit=50")
            .then(r => setItems(r.data.items || []))
            .catch(() => setItems([]));
        // strategy carries next_analysis_date / last_analysis_at so the
        // card can show the countdown header just like on /dashboard.
        api.get("/strategy")
            .then(r => setStrategy(r.data || null))
            .catch(() => setStrategy(null));
    }, []);

    const scrollToAnalysis = () => {
        if (analysisRef.current) {
            analysisRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    const filtered = useMemo(() => {
        if (!items) return null;
        const now = Date.now();
        return items.filter(it => {
            if (filterSource !== "all" && it.source !== filterSource) return false;
            if (filterScore !== "all") {
                const s = it.overall_score ?? 0;
                if (filterScore === "high" && s < 80) return false;
                if (filterScore === "mid"  && (s < 50 || s >= 80)) return false;
                if (filterScore === "low"  && s >= 50) return false;
            }
            if (filterDate !== "all") {
                const t = new Date(it.created_at).getTime();
                const cutoff = filterDate === "7d" ? 7 * 86400000 : 30 * 86400000;
                if (now - t > cutoff) return false;
            }
            return true;
        });
    }, [items, filterDate, filterScore, filterSource]);

    const hasItems = (items?.length ?? 0) > 0;

    return (
        <PageShell testid="reports-page">
            <div data-testid="reports-list">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                    <div>
                        <div className="text-xs uppercase tracking-wider font-mono text-primary-c">AI-аналітика</div>
                        <h1 className="mt-1 font-display font-extrabold text-text-primary"
                            style={{ fontSize: "clamp(32px, 4.5vw, 48px)", letterSpacing: "-0.02em" }}>
                            Тижневі звіти
                        </h1>
                        {csvLimit && (
                            <p className="mt-2 text-sm text-secondary-c" data-testid="reports-counter">
                                <span className="font-mono text-text-primary font-semibold">{csvLimit.used}</span> з <span className="font-mono">{csvLimit.limit}</span> звітів цього місяця
                                {csvLimit.isWarning && !csvLimit.isExhausted && <span className="ml-2 text-warning-c">⚠ майже закінчилось</span>}
                                {csvLimit.isExhausted && <span className="ml-2 text-error-c">— ліміт вичерпано</span>}
                            </p>
                        )}
                    </div>
                    <button
                        onClick={scrollToAnalysis}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold bg-primary text-white hover:bg-primary/90 transition shrink-0"
                        data-testid="reports-create-btn"
                    >
                        <Plus size={16} /> Створити новий звіт
                    </button>
                </div>

                {/* Inline analysis card — previously lived on /dashboard.
                    Moved here in PR #47 so "Create new report" and the
                    empty-state CTA lead users straight to the upload UI
                    on this page instead of bouncing to /dashboard. */}
                <section ref={analysisRef} className="mt-8" data-testid="reports-weekly-section">
                    <WeeklyAnalysisCard
                        nextAnalysisAt={strategy?.next_analysis_date}
                        lastAnalysisAt={strategy?.last_analysis_at}
                    />
                </section>

                {hasItems && (
                    <FilterBar
                        date={filterDate}      setDate={setFilterDate}
                        score={filterScore}    setScore={setFilterScore}
                        source={filterSource}  setSource={setFilterSource}
                    />
                )}

                <div className="mt-6">
                    {items === null
                        ? <ListSkeleton />
                        : !hasItems
                            ? <EmptyState onCreate={scrollToAnalysis} />
                            : filtered.length === 0
                                ? <div className="glass rounded-2xl p-10 text-center text-secondary-c" data-testid="reports-no-match">
                                    Жоден звіт не підходить під фільтри. Спробуйте змінити їх.
                                </div>
                                : (
                                    <ul className="space-y-3" data-testid="reports-items">
                                        {filtered.map(it => (
                                            <ReportCard key={it.report_id} it={it} onClick={() => navigate(`/reports/${it.report_id}`)} />
                                        ))}
                                    </ul>
                                )
                    }
                </div>
            </div>
        </PageShell>
    );
}

function FilterBar({ date, setDate, score, setScore, source, setSource }) {
    const opt = (val, label, current, setter, testid) => (
        <button
            key={val}
            onClick={() => setter(val)}
            data-testid={testid}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition border
                ${current === val
                    ? "bg-primary/20 border-primary/50 text-primary-c"
                    : "bg-surface/30 border-border text-secondary-c hover:text-text-primary hover:border-primary/30"}`}
        >{label}</button>
    );
    return (
        <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="reports-filters">
            <FilterIcon size={14} className="text-secondary-c" />
            <div className="flex flex-wrap gap-2 items-center">
                <span className="text-xs text-faint-c uppercase tracking-wider font-mono">Дата</span>
                {opt("all", "Усі",     date, setDate, "filter-date-all")}
                {opt("7d",  "7 днів",  date, setDate, "filter-date-7d")}
                {opt("30d", "30 днів", date, setDate, "filter-date-30d")}
            </div>
            <div className="flex flex-wrap gap-2 items-center">
                <span className="text-xs text-faint-c uppercase tracking-wider font-mono ml-2">Оцінка</span>
                {opt("all",  "Усі",       score, setScore, "filter-score-all")}
                {opt("high", "80+",       score, setScore, "filter-score-high")}
                {opt("mid",  "50–79",     score, setScore, "filter-score-mid")}
                {opt("low",  "Нижче 50",  score, setScore, "filter-score-low")}
            </div>
            <div className="flex flex-wrap gap-2 items-center">
                <span className="text-xs text-faint-c uppercase tracking-wider font-mono ml-2">Джерело</span>
                {opt("all",  "Усі",  source, setSource, "filter-source-all")}
                {opt("csv",  "CSV",  source, setSource, "filter-source-csv")}
                {opt("live", "MCC", source, setSource, "filter-source-live")}
            </div>
        </div>
    );
}

function ReportCard({ it, onClick }) {
    const score = it.overall_score ?? null;
    const verdict = it.verdict || verdictFromScore(score);
    const { Icon, color } = verdictMeta(verdict);
    const unviewed = !it.viewed_at;
    const totals = it.metrics_summary || {};
    return (
        <li
            onClick={onClick}
            data-testid={`report-item-${it.report_id}`}
            className={`glass rounded-2xl p-5 cursor-pointer transition border
                ${unviewed ? "border-primary/30 bg-primary/[0.03]" : "border-transparent hover:border-primary/20"}`}
        >
            <div className="flex items-start gap-5 flex-wrap md:flex-nowrap">
                <ScoreBubble score={score} verdict={verdict} />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <SourceBadge source={it.source} />
                        <span className="text-xs text-secondary-c font-mono">
                            {new Date(it.created_at).toLocaleDateString("uk-UA", { day: "2-digit", month: "short", year: "numeric" })}
                            {it.period_days ? ` · ${it.period_days} дн.` : ""}
                            {it.currency ? ` · ${it.currency}` : ""}
                        </span>
                        {unviewed && (
                            <span className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-full bg-primary/15 text-primary-c border border-primary/30" data-testid="badge-unviewed">
                                ● Не переглянуто
                            </span>
                        )}
                    </div>
                    <h3 className="mt-2 text-text-primary font-medium text-base leading-snug line-clamp-2">
                        <Icon size={14} className={`inline mr-1 ${color}`} />{it.headline || "Звіт без заголовка"}
                    </h3>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <PreviewMetric label="Витрачено" value={totals.cost != null ? formatMoney(totals.cost, it.currency) : "—"} />
                    <PreviewMetric label="Конверсії" value={totals.conversions != null ? Math.round(totals.conversions).toString() : "—"} />
                    <PreviewMetric label="CPA"       value={totals.cpa != null ? formatMoney(totals.cpa, it.currency) : "—"} />
                </div>
            </div>
        </li>
    );
}

function PreviewMetric({ label, value }) {
    return (
        <div className="px-3 min-w-[88px] hidden md:block">
            <div className="text-[10px] uppercase tracking-wider font-mono text-faint-c">{label}</div>
            <div className="font-mono text-text-primary text-sm font-semibold truncate">{value}</div>
        </div>
    );
}

function ScoreBubble({ score, verdict }) {
    const { color, bg } = verdictMeta(verdict);
    return (
        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 ${bg}`}>
            <span className={`font-mono font-extrabold text-xl ${color}`}>{score ?? "—"}</span>
        </div>
    );
}

function SourceBadge({ source }) {
    const isLive = source === "live";
    const Icon = isLive ? RefreshCw : FileSpreadsheet;
    return (
        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-full bg-surface-hover/40 border border-border text-secondary-c">
            <Icon size={10} />{isLive ? "Авто з MCC" : "CSV"}
        </span>
    );
}

function ListSkeleton() {
    return (
        <ul className="space-y-3" data-testid="reports-skeleton">
            {[0,1,2].map(i => (
                <li key={i} className="glass rounded-2xl p-5 animate-pulse">
                    <div className="flex items-center gap-5">
                        <div className="w-14 h-14 rounded-2xl bg-surface-hover/40" />
                        <div className="flex-1">
                            <div className="h-3 bg-surface-hover/40 rounded w-1/3" />
                            <div className="mt-2 h-4 bg-surface-hover/40 rounded w-4/5" />
                        </div>
                    </div>
                </li>
            ))}
        </ul>
    );
}

function EmptyState({ onCreate }) {
    return (
        <div className="glass rounded-3xl p-10 md:p-14 text-center" data-testid="reports-empty">
            <div className="mx-auto w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center mb-6 relative">
                <Sparkles size={40} className="text-primary-c" />
                <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-success/20 flex items-center justify-center">
                    <TrendingUp size={12} className="text-success-c" />
                </span>
            </div>
            <h2 className="font-display font-bold text-2xl text-text-primary">Ще немає жодного звіту</h2>
            <p className="mt-2 text-secondary-c max-w-md mx-auto">
                Запустіть перший AI-аналіз ваших Google Ads — отримаєте конкретні рекомендації що міняти вже завтра.
            </p>
            <button
                onClick={onCreate}
                className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold bg-primary text-white hover:bg-primary/90 transition"
                data-testid="reports-empty-cta"
            >
                <Plus size={16} /> Створити перший звіт
            </button>
        </div>
    );
}


// ╔════════════════════════════════════════════════════════════════════════╗
// ║ DETAIL PAGE                                                            ║
// ╚════════════════════════════════════════════════════════════════════════╝
function ReportDetailPage({ reportId }) {
    const navigate = useNavigate();
    const { usage } = useUsage();
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showUpgrade, setShowUpgrade] = useState({ open: false, reason: "" });

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get(`/reports/${reportId}`);
                if (!cancelled) setReport(r.data);
            } catch (e) {
                if (!cancelled) setError(e?.response?.data?.detail || "Помилка завантаження");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [reportId]);

    const plan = usage?.plan;
    const canPdf   = plan === "growth" || plan === "scale";
    const canShare = plan === "scale";

    async function handleDelete() {
        if (!window.confirm("Видалити цей звіт? Дію не можна скасувати.")) return;
        try {
            await api.delete(`/reports/${reportId}`);
            toast.success("Звіт видалено");
            navigate("/reports");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Не вдалося видалити звіт");
        }
    }

    function handlePdf() {
        if (!canPdf) {
            setShowUpgrade({ open: true,
                reason: "Експорт у PDF доступний на тарифах Growth та Scale. Перейдіть на вищий тариф, щоб ділитися звітами у красивому форматі." });
            return;
        }
        window.print();
    }

    function handleShare() {
        if (!canShare) {
            setShowUpgrade({ open: true,
                reason: "Публічне посилання (без брендингу AutoAds) доступне лише на тарифі Scale — для агентств і фрилансерів." });
            return;
        }
        toast.info("Скоро — публічне посилання у дорозі.");
    }

    return (
        <PageShell testid="reports-page-detail">
            {loading ? (
                <DetailSkeleton />
            ) : error ? (
                <div className="glass rounded-2xl p-10 text-center text-error-c" data-testid="reports-load-error">{error}</div>
            ) : !report ? (
                <div className="text-secondary-c" data-testid="reports-not-found">Звіт не знайдено.</div>
            ) : report.status === "generating" ? (
                <GeneratingState />
            ) : report.status === "error" ? (
                <ErrorState err={report.error} />
            ) : (
                <ReportContent
                    report={report}
                    canPdf={canPdf}
                    canShare={canShare}
                    onPdf={handlePdf}
                    onShare={handleShare}
                    onDelete={handleDelete}
                />
            )}
            {showUpgrade.open && (
                <UpgradeModal
                    currentPlan={plan}
                    reason={showUpgrade.reason}
                    onClose={() => setShowUpgrade({ open: false, reason: "" })}
                />
            )}
        </PageShell>
    );
}

function ReportContent({ report, canPdf, canShare, onPdf, onShare, onDelete }) {
    const an = report.analysis || {};
    const verdict = report.verdict || verdictFromScore(report.overall_score);
    const { color, bg, Icon, label } = verdictMeta(verdict);

    // ── Action checkbox state (PR #48) ────────────────────────────────────
    // Hydrate from report.actions_done returned by GET /reports/{id}.
    // Optimistic toggle: flip locally, fire API, revert on error.
    const [doneSet, setDoneSet] = React.useState(
        () => new Set((report.actions_done || []).map((d) => d.action_id))
    );
    const [busy, setBusy] = React.useState(() => new Set());

    const isDone = React.useCallback((aid) => doneSet.has(aid), [doneSet]);

    const toggle = React.useCallback(async (aid) => {
        const wasDone = doneSet.has(aid);
        // Optimistic flip
        setDoneSet((prev) => {
            const next = new Set(prev);
            if (wasDone) next.delete(aid); else next.add(aid);
            return next;
        });
        setBusy((prev) => new Set(prev).add(aid));
        try {
            if (wasDone) {
                await api.delete(`/reports/${report.report_id}/actions/${encodeURIComponent(aid)}/done`);
            } else {
                await api.post(`/reports/${report.report_id}/actions/${encodeURIComponent(aid)}/done`);
                // PR #53: dismiss onboarding hint after first successful mark.
                try { localStorage.setItem("recs_done_hint_seen", "1"); } catch { /* noop */ }
            }
        } catch (e) {
            // Revert on failure.
            setDoneSet((prev) => {
                const next = new Set(prev);
                if (wasDone) next.add(aid); else next.delete(aid);
                return next;
            });
            toast.error("Не вдалося зберегти позначку — спробуйте ще раз");
        } finally {
            setBusy((prev) => {
                const next = new Set(prev);
                next.delete(aid);
                return next;
            });
        }
    }, [doneSet, report.report_id]);

    const doneCtx = React.useMemo(() => ({ isDone, toggle, busy }), [isDone, toggle, busy]);

    // ── Top-3 actions for TL;DR block (PR #48) ────────────────────────────
    const topActions = React.useMemo(() => extractTopActions(an), [an]);

    // ── Section counts ────────────────────────────────────────────────────
    const recCounts = countRecs(an.recommendations);
    const burnCounts = countRecs(an.what_burns, { excludeDeadCampaigns: true });
    const worksCounts = countRecs(an.what_works);
    const recDoneCount = Array.from(doneSet).length;  // approximation across whole report

    return (
        <DoneContext.Provider value={doneCtx}>
            <article data-testid="reports-detail">
                {/* ── Header: score + verdict + actions ──────────────────── */}
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <SourceBadge source={report.source} />
                            <span className="text-xs text-secondary-c font-mono">
                                <Calendar size={11} className="inline -mt-0.5 mr-1" />
                                {report.period_start && report.period_end
                                    ? `${formatDate(report.period_start)} → ${formatDate(report.period_end)}`
                                    : `Період: ${report.period_days || 7} днів`}
                                {report.currency ? ` · ${report.currency}` : ""}
                            </span>
                            <span className="text-xs text-faint-c">згенеровано {new Date(report.created_at).toLocaleString("uk-UA")}</span>
                        </div>
                        <h1 className="mt-3 font-display font-extrabold text-text-primary leading-tight" style={{ fontSize: "clamp(24px, 3vw, 36px)" }}>
                            {report.headline || "Звіт"}
                        </h1>
                        <div className="mt-4 flex items-center gap-4 flex-wrap">
                            <div className={`w-20 h-20 rounded-2xl flex items-center justify-center ${bg}`}>
                                <span className={`font-mono font-extrabold text-3xl ${color}`}>{report.overall_score ?? "—"}</span>
                            </div>
                            <div className="flex-1 min-w-[200px]">
                                <div className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full ${bg} ${color}`}>
                                    <Icon size={12} />{label}
                                </div>
                                <p className="mt-2 text-sm text-secondary-c max-w-md">{an.score_explanation}</p>
                            </div>
                        </div>
                    </div>
                    <ActionPanel canPdf={canPdf} canShare={canShare}
                                 onPdf={onPdf} onShare={onShare} onDelete={onDelete} />
                </div>

                {/* ── TL;DR: TOP-3 actions for THIS week (PR #48) ───────────
                    Decision-first surface. Pulls the highest-priority items
                    from across all recommendation groups so the user sees
                    "what to do" before drowning in "what's wrong". */}
                {topActions.length > 0 && (
                    <section
                        className="mt-8 rounded-2xl p-6 md:p-7 border border-primary/30 bg-gradient-to-br from-primary/10 to-transparent"
                        data-testid="reports-tldr"
                    >
                        <h2 className="font-display font-bold text-lg text-text-primary flex items-center gap-2">
                            <Zap size={18} className="text-primary-c" />
                            Цього тижня зробити — ТОП {topActions.length}
                        </h2>
                        <p className="mt-1 text-xs text-secondary-c">
                            Найбільш термінові дії з усього звіту. Виконав → постав галочку.
                        </p>
                        <ol className="mt-4 space-y-3">
                            {topActions.map((a, i) => (
                                <li key={a.aid} className="flex items-start gap-3" data-testid={`tldr-item-${i}`}>
                                    <DoneCheckbox actionId={a.aid} />
                                    <div className={`flex-1 min-w-0 ${doneSet.has(a.aid) ? "opacity-50 line-through decoration-success/40" : ""}`}>
                                        <div className="text-sm font-semibold text-text-primary">
                                            <span className="inline-block w-5 text-faint-c font-mono">{i + 1}.</span>
                                            {a.title}
                                        </div>
                                        {a.impact && (
                                            <div className="text-xs text-success-c mt-0.5 ml-5">→ {a.impact}</div>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </section>
                )}

                {/* ── Always open: high-signal sections ────────────────── */}
                <Section title="Загальна картина" icon={Target} defaultOpen={true}>
                    <KeyMetrics metrics={(an.overview || {}).key_metrics || []} />
                    <p className="mt-5 text-sm text-secondary-c leading-relaxed whitespace-pre-line">{(an.overview || {}).summary}</p>
                </Section>

                {/* "Що горить" stays expanded — these are the issues that need attention NOW. */}
                <Section
                    title="Що горить"
                    icon={Flame}
                    accent="error"
                    defaultOpen={true}
                    count={burnCounts.total}
                >
                    <WhatBurns data={an.what_burns || {}} />
                </Section>

                {/* Recommendations: expanded by default (it IS the deliverable) but
                    individual sub-groups can be collapsed inside Recommendations. */}
                <Section
                    title="Рекомендації"
                    icon={Sparkles}
                    accent="primary"
                    highlight
                    defaultOpen={true}
                    count={recCounts.total}
                    doneCount={recDoneCount}
                >
                    <LearningPhaseCallout analysis={an} />
                    <Recommendations data={an.recommendations || {}} />
                </Section>

                {/* ── Collapsed by default: deep-dive sections ──────────── */}
                <Section
                    title="Що працює"
                    icon={TrendingUp}
                    accent="success"
                    defaultOpen={false}
                    count={worksCounts.total}
                >
                    <WhatWorks data={an.what_works || {}} />
                </Section>

                {an.structure_compliance ? (
                    <Section
                        title="План vs реальність"
                        icon={ClipboardCheck}
                        defaultOpen={false}
                        accent={(() => {
                            const s = (an.structure_compliance || {}).overall_status;
                            if (s === "aligned") return "success";
                            if (s === "diverged") return "error";
                            if (s === "partial") return "primary";
                            return "default";
                        })()}
                    >
                        <StructureCompliance data={an.structure_compliance || {}} />
                    </Section>
                ) : null}

                <Section title="Прогноз" icon={TrendingUp} defaultOpen={false}>
                    <Forecast data={an.forecast || {}} />
                </Section>
            </article>
        </DoneContext.Provider>
    );
}


/**
 * MetricsComparison — PR #56. Renders план-vs-факт comparison table.
 * Shows up to 6 rows (Кліки/Витрати/CTR/Конверсії/CPA/...) with planned,
 * actual, delta_pct, and a colored verdict pill.
 *
 * Empty array → renders nothing (Analyst signals "no forecast" via
 * structure_compliance.summary instead). */
const VERDICT_META = {
    on_track:        { label: "У плані",         cls: "bg-success/15 text-success-c border-success/30" },
    below_plan:      { label: "Нижче плану",     cls: "bg-warning/15 text-warning-c border-warning/30" },
    above_plan:      { label: "Вище плану",      cls: "bg-primary/15 text-primary-c border-primary/30" },
    no_data:         { label: "Без даних",       cls: "bg-surface/40 text-faint-c border-border" },
    not_enough_data: { label: "Мало даних",      cls: "bg-surface/40 text-secondary-c border-border" },
};

const _fmtNum = (n, unit) => {
    if (n == null) return "—";
    if (unit === "%") return `${Number(n).toFixed(1)}%`;
    const isInt = Number.isInteger(n);
    return isInt ? String(n) : Number(n).toFixed(2);
};

const _fmtDelta = (d) => {
    if (d == null) return "—";
    const sign = d > 0 ? "+" : "";
    return `${sign}${Number(d).toFixed(0)}%`;
};

const MetricsComparison = ({ items }) => {
    if (!Array.isArray(items) || items.length === 0) return null;
    return (
        <div data-testid="metrics-comparison">
            <h3 className="text-sm font-bold text-text-primary mb-2 flex items-center gap-2">
                <Target size={14} className="text-primary-c" />
                План vs Факт ({items.length})
            </h3>
            <div className="rounded-xl border border-border overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-surface/40 text-xs uppercase tracking-wider text-faint-c">
                        <tr>
                            <th className="text-left px-3 py-2 font-medium">Метрика</th>
                            <th className="text-right px-3 py-2 font-medium">План</th>
                            <th className="text-right px-3 py-2 font-medium">Факт</th>
                            <th className="text-right px-3 py-2 font-medium">Δ</th>
                            <th className="text-right px-3 py-2 font-medium">Статус</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((it, i) => {
                            const v = VERDICT_META[it.verdict] || VERDICT_META.no_data;
                            const unit = it.unit || "";
                            return (
                                <tr key={i} className="border-t border-border hover:bg-surface/20 transition">
                                    <td className="px-3 py-2 text-text-primary font-medium">
                                        {it.label}
                                        {it.note && (
                                            <div className="text-xs text-secondary-c font-normal mt-0.5 leading-snug">
                                                {it.note}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono text-secondary-c">
                                        {_fmtNum(it.planned, unit)} {unit && unit !== "%" && unit !== "count" ? unit : ""}
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono text-text-primary">
                                        {_fmtNum(it.actual, unit)} {unit && unit !== "%" && unit !== "count" ? unit : ""}
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono text-secondary-c">
                                        {_fmtDelta(it.delta_pct)}
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        <span className={`text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded border ${v.cls}`}>
                                            {v.label}
                                        </span>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

function StructureCompliance({ data }) {
    const status = data?.overall_status || "no_strategy";
    const STATUS_MAP = {
        aligned:     { label: "Все за планом",       Icon: CheckCircle2, color: "text-success-c", bg: "border-success/30 bg-success/5" },
        partial:     { label: "Частково реалізовано", Icon: AlertCircle,  color: "text-primary-c", bg: "border-primary/30 bg-primary/5" },
        diverged:    { label: "Сильні розбіжності",   Icon: XCircle,      color: "text-error-c",   bg: "border-error/30 bg-error/8" },
        no_strategy: { label: "Стратегії немає",      Icon: AlertCircle,  color: "text-faint-c",   bg: "border-border bg-surface/30" },
    };
    const meta = STATUS_MAP[status] || STATUS_MAP.no_strategy;
    const StatusIcon = meta.Icon;

    const missing      = data?.missing_campaigns || [];
    const under        = data?.under_implemented || [];
    const unplanned    = data?.unplanned_additions || [];
    const notes        = data?.notes || [];

    const SEV_BADGE = {
        high:   "bg-error/15 text-error-c border-error/30",
        medium: "bg-warning/15 text-warning-c border-warning/30",
        low:    "bg-surface/40 text-secondary-c border-border",
    };

    return (
        <div data-testid="structure-compliance" className="space-y-5">
            <div className={`rounded-xl border ${meta.bg} p-4 flex items-start gap-3`}>
                <StatusIcon size={22} className={`${meta.color} shrink-0 mt-0.5`} />
                <div className="min-w-0">
                    <div className={`font-display font-bold text-base ${meta.color}`}>{meta.label}</div>
                    {data?.summary ? (
                        <p className="mt-1 text-sm text-secondary-c leading-relaxed">{data.summary}</p>
                    ) : null}
                </div>
            </div>

            <MetricsComparison items={data?.metrics_comparison || []} />

            {missing.length > 0 && (
                <div>
                    <h3 className="text-sm font-bold text-text-primary mb-2 flex items-center gap-2">
                        <XCircle size={14} className="text-error-c" />
                        Не створено зі стратегії ({missing.length})
                    </h3>
                    <ul className="space-y-2">
                        {missing.map((m, i) => (
                            <li key={i} className="rounded-lg border border-border bg-surface/30 p-3">
                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                    <span className="font-mono text-sm text-text-primary">{m.planned_name}</span>
                                    {m.severity ? (
                                        <span className={`text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded border ${SEV_BADGE[m.severity] || SEV_BADGE.low}`}>
                                            {m.severity}
                                        </span>
                                    ) : null}
                                </div>
                                {m.note ? <p className="mt-1.5 text-sm text-secondary-c leading-relaxed">{m.note}</p> : null}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {under.length > 0 && (
                <div>
                    <h3 className="text-sm font-bold text-text-primary mb-2 flex items-center gap-2">
                        <AlertCircle size={14} className="text-warning-c" />
                        Реалізовано не повністю ({under.length})
                    </h3>
                    <ul className="space-y-2">
                        {under.map((u, i) => (
                            <li key={i} className="rounded-lg border border-border bg-surface/30 p-3">
                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                    <span className="font-mono text-sm text-text-primary">{u.target}</span>
                                    <div className="flex items-center gap-2">
                                        {(u.planned != null && u.actual != null) ? (
                                            <span className="text-xs font-mono text-secondary-c">
                                                {u.metric}: <b className="text-text-primary">{u.actual}</b> / {u.planned}
                                            </span>
                                        ) : null}
                                        {u.severity ? (
                                            <span className={`text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded border ${SEV_BADGE[u.severity] || SEV_BADGE.low}`}>
                                                {u.severity}
                                            </span>
                                        ) : null}
                                    </div>
                                </div>
                                {u.note ? <p className="mt-1.5 text-sm text-secondary-c leading-relaxed">{u.note}</p> : null}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {unplanned.length > 0 && (
                <div>
                    <h3 className="text-sm font-bold text-text-primary mb-2 flex items-center gap-2">
                        <Plus size={14} className="text-secondary-c" />
                        Додано поза планом ({unplanned.length})
                    </h3>
                    <ul className="space-y-2">
                        {unplanned.map((a, i) => (
                            <li key={i} className="rounded-lg border border-border bg-surface/30 p-3">
                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                    <span className="font-mono text-sm text-text-primary">{a.target}</span>
                                    {a.kind ? (
                                        <span className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded border border-border bg-surface/40 text-secondary-c">
                                            {a.kind}
                                        </span>
                                    ) : null}
                                </div>
                                {a.note ? <p className="mt-1.5 text-sm text-secondary-c leading-relaxed">{a.note}</p> : null}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {notes.length > 0 && (
                <div>
                    <h3 className="text-sm font-bold text-text-primary mb-2">Додатково</h3>
                    <ul className="space-y-1.5 list-disc list-inside text-sm text-secondary-c">
                        {notes.map((n, i) => <li key={i}>{n}</li>)}
                    </ul>
                </div>
            )}

            {status === "aligned" && missing.length === 0 && under.length === 0 && unplanned.length === 0 && notes.length === 0 ? (
                <p className="text-sm text-faint-c">Структура акаунту повністю відповідає планові — змін не потрібно.</p>
            ) : null}
        </div>
    );
}


/**
 * Collapsible report section (PR #48).
 *
 *   props:
 *     • title, icon, accent, highlight — visual
 *     • defaultOpen   — initial expanded state (default: true for backward compat)
 *     • count         — number rendered after title, e.g. "Рекомендації (18)"
 *     • doneCount     — when set, renders "X з Y виконано" badge in green
 *     • collapsible   — if false, behaves like the old non-collapsible Section
 *
 * Whole header is the toggle target. Body uses CSS `max-height` for a smooth
 * animation without measuring DOM. Empty/zero-count sections collapse silently.
 */
function Section({
    title, icon: Icon, children,
    accent = "default", highlight,
    defaultOpen = true,
    count,
    doneCount,
    collapsible = true,
}) {
    const [open, setOpen] = React.useState(defaultOpen);

    const borderC = {
        default: "border-border",
        success: "border-success/30",
        error:   "border-error/30",
        primary: "border-primary/30",
    }[accent] || "border-border";
    const iconC = accent === "success" ? "text-success-c"
              : accent === "error"   ? "text-error-c"
              : accent === "primary" ? "text-primary-c"
              : "text-secondary-c";

    const Header = collapsible ? "button" : "div";
    const headerProps = collapsible
        ? { type: "button", onClick: () => setOpen((v) => !v), "aria-expanded": open }
        : {};

    return (
        <section
            className={`mt-8 glass rounded-2xl border ${borderC} ${highlight ? "shadow-[0_0_0_1px_rgba(109,94,248,0.15)]" : ""}`}
            data-testid={`section-${(title || "").toLowerCase().replace(/\s+/g, "-")}`}
        >
            <Header
                {...headerProps}
                className={`w-full flex items-center justify-between gap-3 p-6 md:p-8 ${collapsible ? "cursor-pointer hover:bg-surface-hover/40 transition rounded-2xl" : ""}`}
            >
                <h2 className="font-display font-bold text-xl text-text-primary flex items-center gap-2 text-left">
                    <Icon size={18} className={iconC} />
                    <span>{title}</span>
                    {typeof count === "number" && count > 0 && (
                        <span className="text-sm font-mono text-faint-c font-normal">
                            ({count})
                        </span>
                    )}
                    {typeof doneCount === "number" && typeof count === "number" && count > 0 && (
                        <span className="ml-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-success/15 text-success-c">
                            {doneCount} з {count} виконано
                        </span>
                    )}
                </h2>
                {collapsible && (
                    <ChevronDown
                        size={20}
                        className={`text-secondary-c shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
                    />
                )}
            </Header>
            {open && (
                <div className="px-6 md:px-8 pb-6 md:pb-8 -mt-2">
                    {children}
                </div>
            )}
        </section>
    );
}


function KeyMetrics({ metrics }) {
    if (!metrics?.length) return <p className="text-sm text-faint-c">Метрик не отримано.</p>;
    return (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="key-metrics">
            {metrics.map((m, i) => (
                <div key={i} className="rounded-xl border border-border bg-surface/30 p-4">
                    <div className="text-xs text-secondary-c uppercase tracking-wider font-mono">{m.label}</div>
                    <div className="mt-1 font-mono font-bold text-xl text-text-primary">
                        {m.value}{m.unit ? <span className="text-secondary-c text-sm ml-1">{m.unit}</span> : null}
                    </div>
                    <TrendBadge dir={m.trend_direction} pct={m.trend_pct} note={m.comment} />
                </div>
            ))}
        </div>
    );
}

function TrendBadge({ dir, pct, note }) {
    if (dir == null && pct == null) {
        return note ? <div className="mt-1 text-[11px] text-faint-c">{note}</div> : null;
    }
    const I = dir === "up" ? TrendingUp : dir === "down" ? TrendingDown : Minus;
    const isPos = (dir === "up" && pct > 0) || (dir === "down" && pct < 0);
    const color = isPos ? "text-success-c" : (dir === "flat" ? "text-faint-c" : "text-error-c");
    const sign = pct == null ? "" : (pct > 0 ? "+" : "");
    return (
        <div className={`mt-1 flex items-center gap-1 text-[11px] font-mono ${color}`}>
            <I size={11} />{pct != null ? `${sign}${pct}%` : "—"}
            {note ? <span className="text-faint-c ml-1 truncate">· {note}</span> : null}
        </div>
    );
}


function WhatWorks({ data }) {
    const blocks = [
        { key: "top_campaigns",    title: "Топ-кампанії",        items: data.top_campaigns },
        { key: "top_keywords",     title: "Топ-ключові слова",    items: data.top_keywords },
        { key: "top_search_terms", title: "Топ-пошукові запити",  items: data.top_search_terms },
    ];
    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {blocks.map(b => (
                <div key={b.key} className="rounded-xl border border-success/20 bg-success/5 p-4" data-testid={`works-${b.key}`}>
                    <div className="text-xs uppercase tracking-wider font-mono text-success-c">{b.title}</div>
                    {!b.items?.length ? (
                        <p className="mt-3 text-sm text-faint-c">Поки немає лідерів у цій категорії.</p>
                    ) : (
                        <ul className="mt-3 space-y-3">
                            {b.items.slice(0, 5).map((it, i) => (
                                <li key={i} className="text-sm">
                                    <div className="font-medium text-text-primary truncate">{it.name || it.text || it.term}</div>
                                    {it.match_type && <span className="text-[10px] font-mono text-faint-c">[{it.match_type}]</span>}
                                    <div className="text-xs text-secondary-c">{it.metric ? `${it.metric}: ` : ""}<span className="text-text-primary font-mono">{it.value || it.conversions || it.cost}</span></div>
                                    {it.note && <div className="mt-1 text-[11px] text-faint-c leading-snug">{it.note}</div>}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            ))}
        </div>
    );
}


function WhatBurns({ data }) {
    const sections = [
        { key: "wasted_campaigns", title: "Кампанії, що зливають бюджет", render: WastedCampaign },
        { key: "wasted_keywords",  title: "Ключові слова без віддачі",    render: WastedKeyword },
        { key: "anomalies",        title: "Аномалії",                      render: AnomalyCard },
        { key: "irrelevant_search_terms", title: "Нерелевантні пошукові запити", render: IrrelevantTerm },
    ];
    // PR #55: filter out items about REMOVED/PAUSED campaigns — they belong
    // in plan_vs_reality.added_outside_plan, not in "what burns this week".
    const filtered = {};
    for (const s of sections) {
        filtered[s.key] = (data[s.key] || []).filter((it) => !_isAboutDeadCampaign(it));
    }
    const hasAny = sections.some(s => (filtered[s.key] || []).length > 0);
    if (!hasAny) {
        return <p className="text-sm text-faint-c">Жодних серйозних проблем не виявлено — кампанії в нормі.</p>;
    }
    return (
        <div className="space-y-5">
            {sections.map(s => (filtered[s.key]?.length ? (
                <div key={s.key} data-testid={`burns-${s.key}`}>
                    <div className="text-xs uppercase tracking-wider font-mono text-error-c mb-2">{s.title}</div>
                    <div className="space-y-2">
                        {filtered[s.key].map((it, i) => <s.render key={i} it={it} />)}
                    </div>
                </div>
            ) : null))}
        </div>
    );
}

function severityFrame(sev) {
    return sev === "high"   ? "border-error/40 bg-error/10"
        : sev === "medium" ? "border-warning/40 bg-warning/5"
        :                    "border-border bg-surface/30";
}
/**
 * Severity badge. Supports legacy levels (high/medium/low) and new PR #49
 * levels (urgent/important/later) — they map to the same visual styles.
 */
function severityBadge(sev) {
    const norm = { urgent: "high", important: "medium", later: "low" }[sev] || sev;
    const cls = {
        high:   "bg-error/15 text-error-c border-error/30",
        medium: "bg-warning/15 text-warning-c border-warning/30",
        low:    "bg-surface-hover text-secondary-c border-border",
    }[norm] || "bg-surface-hover text-secondary-c border-border";
    const labels = { high: "Терміново", medium: "Середньо", low: "Можна потім" };
    return <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded border ${cls}`}>{labels[norm] || sev}</span>;
}

/**
 * Confidence badge (PR #49). Tells the user how reliable a recommendation is.
 *   • high   → solid evidence; act with confidence
 *   • medium → pattern is there, sample is borderline
 *   • low    → guess; ideally the AI puts these in watch_signals, but if a
 *               'low' shows up here we mark it clearly so user is warned.
 */
function confidenceBadge(conf) {
    if (!conf) return null;
    const cls = {
        high:   "bg-primary/10 text-primary-c border-primary/30",
        medium: "bg-surface-hover text-secondary-c border-border",
        low:    "bg-warning/10 text-warning-c border-warning/30",
    }[conf] || "bg-surface-hover text-secondary-c border-border";
    const labels = {
        high:   "Впевненість висока",
        medium: "Впевненість середня",
        low:    "Впевненість низька — спостерігаємо",
    };
    return (
        <span
            className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded border ${cls}`}
            title={labels[conf]}
        >
            {conf === "high" ? "✓ " : conf === "low" ? "? " : ""}
            {conf}
        </span>
    );
}

function WastedCampaign({ it }) {
    return (
        <div className={`rounded-lg p-3 border ${severityFrame("high")}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="font-medium text-text-primary text-sm">{it.name}</div>
                <div className="font-mono text-xs text-error-c">−{it.wasted}</div>
            </div>
            <div className="mt-1 text-xs text-secondary-c">{it.issue}</div>
            {it.evidence && <div className="mt-1 text-[11px] text-faint-c">📊 {it.evidence}</div>}
        </div>
    );
}
function WastedKeyword({ it }) {
    return (
        <div className={`rounded-lg p-3 border ${severityFrame("medium")}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-sm">
                    <span className="font-mono text-text-primary">{it.text}</span>
                    {it.match_type && <span className="text-[10px] font-mono text-faint-c ml-2">[{it.match_type}]</span>}
                </div>
                <div className="font-mono text-xs text-warning-c">{it.cost} · {it.conversions} конв.</div>
            </div>
            {it.evidence && <div className="mt-1 text-[11px] text-faint-c">{it.evidence}</div>}
        </div>
    );
}
function AnomalyCard({ it }) {
    return (
        <div className={`rounded-lg p-3 border ${severityFrame(it.severity)}`}>
            <div className="flex items-center gap-2 flex-wrap">
                <AlertTriangle size={14} className={it.severity === "high" ? "text-error-c" : "text-warning-c"} />
                <div className="font-medium text-text-primary text-sm flex-1">{it.title}</div>
                {severityBadge(it.severity)}
            </div>
            <div className="mt-1 text-xs text-secondary-c">{it.detail}</div>
            {it.metric_snapshot && <div className="mt-1 text-[11px] font-mono text-faint-c">{it.metric_snapshot}</div>}
        </div>
    );
}
function IrrelevantTerm({ it }) {
    return (
        <div className={`rounded-lg p-3 border ${severityFrame("medium")}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="font-mono text-text-primary text-sm">&quot;{it.term}&quot;</div>
                <div className="font-mono text-xs text-warning-c">−{it.cost_wasted}</div>
            </div>
            <div className="mt-1 text-xs text-secondary-c">{it.why_irrelevant}</div>
        </div>
    );
}


function Recommendations({ data }) {
    /**
     * Each group has a deterministic action-id strategy (PR #48) so that
     * checkbox state survives report reloads. `aidOf(item)` returns the
     * stable hash used by ActionRow + DoneContext.
     */
    const groups = [
        {
            key: "negative_keywords", title: "Мінус-слова", copyAll: true,
            render: NegKwCard, defaultOpen: true,
            aidOf: (it) => actionId("neg", it.keyword),
        },
        {
            key: "pause_keywords", title: "Поставити на паузу", copyAll: false,
            render: PauseKwCard, defaultOpen: false,
            aidOf: (it) => actionId("pause", it.keyword),
        },
        {
            key: "budget_reallocation", title: "Бюджет", copyAll: false,
            render: BudgetCard, defaultOpen: false,
            aidOf: (it) => actionId("budget", `${it.action || ""}:${it.from || ""}→${it.to || ""}`),
        },
        {
            key: "ad_copy_rewrites", title: "Тексти оголошень", copyAll: false,
            render: AdCopyCard, defaultOpen: false,
            aidOf: (it) => actionId("adcopy", it.ad_group || it.target || JSON.stringify(it).slice(0, 60)),
        },
        {
            key: "new_keywords_to_add", title: "Нові ключові слова", copyAll: true,
            render: NewKwCard, defaultOpen: false,
            aidOf: (it) => actionId("newkw", it.keyword),
        },
    ];
    const hasAny = groups.some(g => (data[g.key] || []).length > 0);
    if (!hasAny) {
        return <p className="text-sm text-faint-c">Жодних активних дій цього тижня не потрібно — продовжуйте у тому ж дусі.</p>;
    }

    function copyList(key, items) {
        const text = (items || []).map(i => i.keyword).filter(Boolean).join("\n");
        if (!text) { toast.warning("Нічого копіювати"); return; }
        navigator.clipboard.writeText(text).then(
            () => toast.success(`Скопійовано ${items.length} ${key === "negative_keywords" ? "мінус-слів" : "ключів"} — вставте у Google Ads`),
            () => toast.error("Не вдалося скопіювати")
        );
    }

    return (
        <div className="space-y-4">
            <DoneOnboardingHint />
            {groups.map(g => (data[g.key]?.length
                ? <RecGroup key={g.key} g={g} items={data[g.key]} onCopy={() => copyList(g.key, data[g.key])} />
                : null
            ))}
        </div>
    );
}

/**
 * DoneOnboardingHint — small dismissible callout shown ONCE per user
 * explaining the checkbox feature. Persisted via localStorage so it
 * doesn't keep popping up. Auto-hides as soon as the user toggles
 * any checkbox (no point teaching what they already learned). */
const DoneOnboardingHint = () => {
    const { isDone } = React.useContext(DoneContext);
    const [show, setShow] = React.useState(() => {
        try { return localStorage.getItem("recs_done_hint_seen") !== "1"; }
        catch { return true; }
    });
    // Hide as soon as user has marked at least 1 action — they got it.
    const hasAny = React.useContext(DoneContext);
    React.useEffect(() => {
        if (typeof isDone !== "function") return;
        // We don't have direct access to doneSet here, so we just check
        // when the component mounts. The dismiss-on-click below handles
        // explicit hide. (Auto-hide on first toggle handled in toggle().)
    }, [hasAny, isDone]);

    const dismiss = () => {
        try { localStorage.setItem("recs_done_hint_seen", "1"); } catch { /* noop */ }
        setShow(false);
    };
    if (!show) return null;
    return (
        <div
            className="rounded-lg border border-primary/25 bg-primary/5 p-3 flex items-start gap-2.5 text-sm"
            data-testid="done-onboarding-hint"
        >
            <Check size={16} className="text-primary-c shrink-0 mt-0.5" />
            <div className="flex-1 text-secondary-c leading-relaxed">
                <span className="font-semibold text-text-primary">Підказка:</span>{" "}
                поряд із кожною рекомендацією є квадратик. Натискай — і виконані поради{" "}
                <span className="font-semibold text-text-primary">не повторюватимуться</span>{" "}
                у наступних звітах, AI бачитиме що ти вже зробив.
            </div>
            <button
                type="button"
                onClick={dismiss}
                aria-label="Зрозуміло"
                className="shrink-0 text-faint-c hover:text-text-primary transition"
            >
                <X size={16} />
            </button>
        </div>
    );
};

/**
 * Inline callout shown above Recommendations when the report is in a
 * "learning phase" or "low data" state (PR #49). Pulls hints from
 * analysis.statistical_context to explain why some sections may be empty
 * or marked low-confidence. */
function LearningPhaseCallout({ analysis }) {
    const ctx = analysis?.statistical_context;
    if (!ctx) return null;
    const learning = ctx.is_learning_phase;
    const trackingBad = ctx.tracking_suspect;
    if (!learning && !trackingBad) return null;
    return (
        <div className="mb-4 rounded-xl border border-warning/30 bg-warning/5 p-4" data-testid="learning-callout">
            <div className="flex items-start gap-2.5">
                <AlertCircle size={18} className="text-warning-c shrink-0 mt-0.5" />
                <div className="flex-1 text-sm">
                    {trackingBad && (
                        <div className="font-semibold text-text-primary mb-1">
                            Підозра: conversion tracking не налаштовано
                        </div>
                    )}
                    {learning && !trackingBad && (
                        <div className="font-semibold text-text-primary mb-1">
                            Фаза навчання Google ({ctx.period_days} {ctx.period_days === 1 ? "день" : "днів"} даних)
                        </div>
                    )}
                    <div className="text-secondary-c leading-relaxed">
                        {trackingBad
                            ? `${ctx.totals?.clicks || 0} кліків і 0 конверсій — статистично малоймовірно для працюючого pixel. Перевір conversion tracking ПЕРЕД тим як міняти ключові слова.`
                            : `Кампанія ще навчається — Google потребує 7-30 днів для калібрування алгоритму. Поточних даних (${ctx.totals?.clicks || 0} кліків) недостатньо для тактичних рішень типу "паузнути ключ". Фокус цього звіту: structure issues + tracking, а не оптимізація.`}
                    </div>
                    {ctx.decision_thresholds?.min_clicks_for_kw_decision && (
                        <div className="text-xs text-faint-c mt-2 font-mono">
                            Пороги для рішень: ≥{ctx.decision_thresholds.min_clicks_for_kw_decision} кліків,
                            {ctx.decision_thresholds.min_spend_for_kw_pause
                                ? ` ≥${ctx.decision_thresholds.min_spend_for_kw_pause} витрат на ключ`
                                : ""}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

/** Collapsible sub-group inside Recommendations (PR #48). */
function RecGroup({ g, items, onCopy }) {
    const { isDone } = React.useContext(DoneContext);
    const [open, setOpen] = React.useState(g.defaultOpen);
    const doneN = items.filter((it) => isDone(g.aidOf(it))).length;

    return (
        <div className="rounded-xl border border-border bg-surface/20" data-testid={`recs-${g.key}`}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between gap-3 p-3.5 hover:bg-surface-hover/40 transition rounded-xl"
                aria-expanded={open}
            >
                <h3 className="font-display font-semibold text-base text-text-primary flex items-center gap-2">
                    <ChevronDown
                        size={16}
                        className={`text-secondary-c transition-transform ${open ? "rotate-0" : "-rotate-90"}`}
                    />
                    {g.title}
                    <span className="text-xs text-faint-c font-mono font-normal">({items.length})</span>
                    {doneN > 0 && (
                        <span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full bg-success/15 text-success-c">
                            {doneN}/{items.length}
                        </span>
                    )}
                </h3>
                {g.copyAll && open && (
                    <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); onCopy(); }}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onCopy(); } }}
                        className="inline-flex items-center gap-1.5 text-xs text-primary-c hover:underline font-medium cursor-pointer"
                        data-testid={`recs-copy-${g.key}`}
                    >
                        <CopyIcon size={12} /> Скопіювати всі
                    </span>
                )}
            </button>
            {open && (
                <div className="px-3.5 pb-3.5 space-y-2.5">
                    {items.map((it, i) => (
                        <ActionRow key={i} actionId={g.aidOf(it)}>
                            <g.render it={it} />
                        </ActionRow>
                    ))}
                </div>
            )}
        </div>
    );
}

function NegKwCard({ it }) {
    return (
        <div className="rounded-lg p-3 border border-border bg-surface/30">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="font-mono text-sm text-text-primary">
                    {it.keyword}
                    {it.match_type && <span className="text-[10px] text-faint-c ml-2">[{it.match_type}]</span>}
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                    {severityBadge(it.priority)}
                    {confidenceBadge(it.confidence)}
                </div>
            </div>
            <div className="mt-1 text-xs text-secondary-c">{it.reason}</div>
            {it.expected_impact && <div className="mt-1 text-[11px] text-success-c">→ {it.expected_impact}</div>}
        </div>
    );
}
function PauseKwCard({ it }) {
    return (
        <div className="rounded-lg p-3 border border-border bg-surface/30">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="font-mono text-sm text-text-primary">{it.keyword}</div>
                <div className="flex items-center gap-1.5 flex-wrap">
                    {severityBadge(it.priority)}
                    {confidenceBadge(it.confidence)}
                </div>
            </div>
            <div className="mt-1 text-xs text-secondary-c">{it.reason}</div>
            {it.expected_impact && <div className="mt-1 text-[11px] text-success-c">→ {it.expected_impact}</div>}
        </div>
    );
}
function BudgetCard({ it }) {
    return (
        <div className="rounded-lg p-3 border border-border bg-surface/30">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-sm text-text-primary">
                    <span className="inline-block px-1.5 py-0.5 rounded font-mono text-[10px] bg-surface-hover/40 mr-2 uppercase">{it.action}</span>
                    <span className="font-mono text-faint-c">{it.from}</span>
                    <span className="mx-2 text-primary-c">→</span>
                    <span className="font-mono">{it.to}</span>
                    {it.amount && <span className="text-xs text-secondary-c ml-2">({it.amount})</span>}
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                    {severityBadge(it.priority)}
                    {confidenceBadge(it.confidence)}
                </div>
            </div>
            <div className="mt-1 text-xs text-secondary-c">{it.reason}</div>
            {it.expected_impact && <div className="mt-1 text-[11px] text-success-c">→ {it.expected_impact}</div>}
        </div>
    );
}
function AdCopyCard({ it }) {
    const copySuggestion = (text) => {
        navigator.clipboard.writeText(text).then(() => toast.success("Скопійовано"));
    };
    return (
        <div className="rounded-lg p-3 border border-border bg-surface/30">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-sm font-medium text-text-primary">{it.target}</div>
                <div className="flex items-center gap-1.5 flex-wrap">
                    {severityBadge(it.priority)}
                    {confidenceBadge(it.confidence)}
                </div>
            </div>
            <div className="mt-1 text-xs text-secondary-c">{it.reason}</div>
            {it.suggestions && (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                    {it.suggestions.headlines?.length > 0 && (
                        <div>
                            <div className="text-[10px] uppercase tracking-wider font-mono text-faint-c mb-1">Заголовки (≤30)</div>
                            <ul className="space-y-1">
                                {it.suggestions.headlines.map((h, i) => (
                                    <li key={i} className="flex items-center justify-between gap-2 text-xs bg-surface-hover/30 rounded px-2 py-1">
                                        <span className="font-mono truncate text-text-primary">{h}</span>
                                        <button onClick={() => copySuggestion(h)} className="text-faint-c hover:text-primary-c" title="Скопіювати">
                                            <CopyIcon size={11} />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {it.suggestions.descriptions?.length > 0 && (
                        <div>
                            <div className="text-[10px] uppercase tracking-wider font-mono text-faint-c mb-1">Описи (≤90)</div>
                            <ul className="space-y-1">
                                {it.suggestions.descriptions.map((d, i) => (
                                    <li key={i} className="flex items-center justify-between gap-2 text-xs bg-surface-hover/30 rounded px-2 py-1">
                                        <span className="font-mono truncate text-text-primary">{d}</span>
                                        <button onClick={() => copySuggestion(d)} className="text-faint-c hover:text-primary-c" title="Скопіювати">
                                            <CopyIcon size={11} />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
            {it.expected_impact && <div className="mt-2 text-[11px] text-success-c">→ {it.expected_impact}</div>}
        </div>
    );
}
function NewKwCard({ it }) {
    return (
        <div className="rounded-lg p-3 border border-border bg-surface/30">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="font-mono text-sm text-text-primary">
                    {it.keyword}
                    {it.match_type && <span className="text-[10px] text-faint-c ml-2">[{it.match_type}]</span>}
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                    {severityBadge(it.priority)}
                    {confidenceBadge(it.confidence)}
                </div>
            </div>
            <div className="mt-1 text-xs text-secondary-c">{it.rationale}</div>
        </div>
    );
}


function Forecast({ data }) {
    return (
        <div className="space-y-5">
            <ForecastBlock title="Якщо нічого не міняти" tone="warning" text={data.if_no_action} />
            <ForecastBlock title="Якщо застосувати high-priority рекомендації" tone="success" text={data.if_act_on_high_priority} />
            {data.expected_metrics_delta?.length > 0 && (
                <div data-testid="forecast-deltas">
                    <div className="text-xs uppercase tracking-wider font-mono text-secondary-c mb-2">Очікувані зміни метрик</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {data.expected_metrics_delta.map((d, i) => (
                            <div key={i} className="rounded-lg p-3 border border-border bg-surface/30">
                                <div className="text-xs text-secondary-c uppercase tracking-wider font-mono">{d.label}</div>
                                <div className="mt-1 flex items-center gap-2 font-mono text-sm">
                                    <span className="text-faint-c">{d.current}</span>
                                    <span className="text-primary-c">→</span>
                                    <span className="text-text-primary font-semibold">{d.predicted}</span>
                                    {d.change && <span className="ml-auto text-success-c text-xs">{d.change}</span>}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {data.watch_signals?.length > 0 && (
                <div data-testid="forecast-signals">
                    <div className="text-xs uppercase tracking-wider font-mono text-secondary-c mb-2">На що звернути увагу</div>
                    <ul className="space-y-1.5">
                        {data.watch_signals.map((s, i) => (
                            <li key={i} className="text-sm text-secondary-c flex items-start gap-2">
                                <span className="text-primary-c mt-1">›</span>{s}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
function ForecastBlock({ title, tone, text }) {
    if (!text) return null;
    const cls = tone === "success" ? "border-success/30 bg-success/5" : "border-warning/30 bg-warning/5";
    return (
        <div className={`rounded-xl p-4 border ${cls}`}>
            <div className="text-xs uppercase tracking-wider font-mono text-secondary-c">{title}</div>
            <p className="mt-1.5 text-sm text-text-primary leading-relaxed whitespace-pre-line">{text}</p>
        </div>
    );
}


function ActionPanel({ canPdf, canShare, onPdf, onShare, onDelete }) {
    return (
        <div className="flex flex-col gap-2 print:hidden shrink-0" data-testid="report-actions">
            <ActionButton icon={FileDown} label="Експорт у PDF" onClick={onPdf}
                          locked={!canPdf} lockedHint="Доступно на Growth/Scale" testid="action-pdf" />
            <ActionButton icon={Share2} label="Поділитись" onClick={onShare}
                          locked={!canShare} lockedHint="Доступно на Scale" testid="action-share" />
            <ActionButton icon={Trash2} label="Видалити" onClick={onDelete} danger testid="action-delete" />
        </div>
    );
}

function ActionButton({ icon: Icon, label, onClick, locked, lockedHint, danger, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            title={locked ? lockedHint : label}
            className={`relative inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition
                ${danger ? "border-error/30 text-error-c hover:bg-error/10" :
                  locked ? "border-border bg-surface/30 text-faint-c hover:text-secondary-c" :
                          "border-border bg-surface/30 text-text-primary hover:bg-surface-hover/40 hover:border-primary/30"}`}
        >
            <Icon size={14} />{label}
            {locked && <Lock size={10} className="text-warning-c" />}
        </button>
    );
}


function GeneratingState() {
    return (
        <div className="glass rounded-2xl p-10 flex items-center gap-4" data-testid="reports-generating">
            <Loader2 size={28} className="text-primary-c animate-spin" />
            <div>
                <div className="text-text-primary font-medium">Звіт ще генерується…</div>
                <div className="text-xs text-secondary-c">Зачекайте 30-90 секунд і оновіть сторінку.</div>
            </div>
        </div>
    );
}
function ErrorState({ err }) {
    return (
        <div className="glass rounded-2xl p-7 border border-error/30" data-testid="reports-error">
            <div className="text-error-c font-medium flex items-center gap-2">
                <AlertTriangle size={16} /> Помилка під час аналізу
            </div>
            <div className="mt-2 text-sm text-secondary-c">{err}</div>
        </div>
    );
}
function DetailSkeleton() {
    return (
        <div className="animate-pulse">
            <div className="h-3 bg-surface-hover/40 rounded w-1/3" />
            <div className="mt-4 h-9 bg-surface-hover/40 rounded w-3/4" />
            <div className="mt-3 h-20 bg-surface-hover/40 rounded w-1/2" />
            <div className="mt-8 h-48 glass rounded-2xl" />
            <div className="mt-6 h-64 glass rounded-2xl" />
        </div>
    );
}


function PageShell({ children, testid }) {
    return (
        <AppShell testid={testid} containerClass="container-mx py-10 max-w-5xl">
            {children}
        </AppShell>
    );
}


function verdictFromScore(s) {
    if (s == null) return "neutral";
    if (s >= 70) return "positive";
    if (s >= 40) return "neutral";
    return "alarming";
}
function verdictMeta(v) {
    if (v === "positive") return { Icon: TrendingUp,    color: "text-success-c", bg: "bg-success/15", label: "Позитивно" };
    if (v === "alarming") return { Icon: AlertTriangle, color: "text-error-c",   bg: "bg-error/15",   label: "Тривожно" };
    return                       { Icon: Minus,         color: "text-warning-c", bg: "bg-warning/15", label: "Нейтрально" };
}
function formatDate(s) {
    try { return new Date(s).toLocaleDateString("uk-UA", { day: "2-digit", month: "short" }); }
    catch { return s; }
}
function formatMoney(v, currency) {
    if (typeof v === "string") return v;
    const n = Number(v) || 0;
    return `${n.toFixed(2)} ${currency || ""}`.trim();
}